import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Plus, Edit, Trash2, Save, X, Flag } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface FeatureFlag {
  id?: string;
  flag_key: string;
  title: string;
  description?: string;
  is_enabled: boolean;
  user_segments: string[];
  rollout_percentage: number;
}

export default function FeatureFlagsManager() {
  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingFlag, setEditingFlag] = useState<FeatureFlag | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const defaultFlag: FeatureFlag = {
    flag_key: '',
    title: '',
    description: '',
    is_enabled: false,
    user_segments: ['all'],
    rollout_percentage: 100
  };

  useEffect(() => {
    loadFlags();
  }, []);

  const loadFlags = async () => {
    try {
      const { data, error } = await supabase
        .from('feature_flags')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      setFlags(data || []);
    } catch (error) {
      console.error('Error loading flags:', error);
      toast.error('Failed to load feature flags');
    } finally {
      setLoading(false);
    }
  };

  const saveFlag = async (flag: FeatureFlag) => {
    try {
      if (flag.id) {
        const { error } = await supabase
          .from('feature_flags')
          .update(flag)
          .eq('id', flag.id);
        if (error) throw error;
        toast.success('Feature flag updated successfully');
      } else {
        const { error } = await supabase
          .from('feature_flags')
          .insert([flag]);
        if (error) throw error;
        toast.success('Feature flag created successfully');
      }
      
      loadFlags();
      setEditingFlag(null);
      setIsCreating(false);
    } catch (error) {
      console.error('Error saving flag:', error);
      toast.error('Failed to save feature flag');
    }
  };

  const deleteFlag = async (id: string) => {
    try {
      const { error } = await supabase
        .from('feature_flags')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      toast.success('Feature flag deleted successfully');
      loadFlags();
    } catch (error) {
      console.error('Error deleting flag:', error);
      toast.error('Failed to delete feature flag');
    }
  };

  const toggleFlag = async (flag: FeatureFlag) => {
    try {
      const { error } = await supabase
        .from('feature_flags')
        .update({ is_enabled: !flag.is_enabled })
        .eq('id', flag.id);
      
      if (error) throw error;
      toast.success(`Feature flag ${flag.is_enabled ? 'disabled' : 'enabled'}`);
      loadFlags();
    } catch (error) {
      console.error('Error toggling flag:', error);
      toast.error('Failed to toggle feature flag');
    }
  };

  const FlagForm = ({ flag, onSave, onCancel }: { 
    flag: FeatureFlag; 
    onSave: (flag: FeatureFlag) => void; 
    onCancel: () => void; 
  }) => {
    const [formData, setFormData] = useState(flag);

    return (
      <Card className="mb-4">
        <CardHeader>
          <CardTitle>{flag.id ? 'Edit Feature Flag' : 'Create New Feature Flag'}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="flag_key">Flag Key</Label>
            <Input
              id="flag_key"
              value={formData.flag_key}
              onChange={(e) => setFormData({ ...formData, flag_key: e.target.value })}
              placeholder="hypnotic_kaleidoscope_enabled"
            />
          </div>
          
          <div>
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Hypnotic Kaleidoscope Feature"
            />
          </div>
          
          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Enable the hypnotic kaleidoscope experience for manifestation"
            />
          </div>
          
          <div className="flex items-center space-x-2">
            <Switch
              id="is_enabled"
              checked={formData.is_enabled}
              onCheckedChange={(checked) => setFormData({ ...formData, is_enabled: checked })}
            />
            <Label htmlFor="is_enabled">Enabled</Label>
          </div>
          
          <div>
            <Label htmlFor="rollout_percentage">Rollout Percentage: {formData.rollout_percentage}%</Label>
            <Slider
              id="rollout_percentage"
              min={0}
              max={100}
              step={5}
              value={[formData.rollout_percentage]}
              onValueChange={(value) => setFormData({ ...formData, rollout_percentage: value[0] })}
              className="mt-2"
            />
          </div>
          
          <div>
            <Label htmlFor="user_segments">User Segments (comma separated)</Label>
            <Input
              id="user_segments"
              value={formData.user_segments.join(', ')}
              onChange={(e) => setFormData({ 
                ...formData, 
                user_segments: e.target.value.split(',').map(s => s.trim()).filter(s => s) 
              })}
              placeholder="all, premium, admin"
            />
          </div>
          
          <div className="flex gap-2">
            <Button onClick={() => onSave(formData)}>
              <Save className="h-4 w-4 mr-2" />
              Save
            </Button>
            <Button variant="outline" onClick={onCancel}>
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return <div>Loading feature flags...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Feature Flags Management</h2>
        <Button onClick={() => setIsCreating(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add New Flag
        </Button>
      </div>
      
      <div className="p-4 bg-muted rounded-lg">
        <h3 className="font-semibold mb-2">🎛️ Instant Feature Control</h3>
        <p className="text-sm text-muted-foreground">
          Feature flags let you enable/disable features instantly, control rollout percentages for gradual releases, 
          and target specific user segments. Perfect for A/B testing and safe deployments.
        </p>
      </div>
      
      {isCreating && (
        <FlagForm
          flag={defaultFlag}
          onSave={saveFlag}
          onCancel={() => setIsCreating(false)}
        />
      )}
      
      {editingFlag && (
        <FlagForm
          flag={editingFlag}
          onSave={saveFlag}
          onCancel={() => setEditingFlag(null)}
        />
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {flags.map((flag) => (
          <Card key={flag.id} className={!flag.is_enabled ? 'opacity-60' : ''}>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Flag className="h-4 w-4" />
                    {flag.title}
                  </CardTitle>
                  <code className="text-xs text-muted-foreground">{flag.flag_key}</code>
                </div>
                <div className="flex gap-1">
                  <Switch
                    checked={flag.is_enabled}
                    onCheckedChange={() => toggleFlag(flag)}
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setEditingFlag(flag)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => flag.id && deleteFlag(flag.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-3">{flag.description}</p>
              
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">Rollout:</span>
                  <Badge variant="outline">{flag.rollout_percentage}%</Badge>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">Segments:</span>
                  <div className="flex gap-1">
                    {flag.user_segments.map((segment, index) => (
                      <Badge key={index} variant="secondary" className="text-xs">
                        {segment}
                      </Badge>
                    ))}
                  </div>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">Status:</span>
                  <Badge variant={flag.is_enabled ? 'default' : 'secondary'}>
                    {flag.is_enabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}